import { cn } from '@atlas/ui-primitives'
import { TriangleAlert } from 'lucide-react'
import type { ReactNode } from 'react'

/**
 * Props for the {@link ErrorState} component.
 *
 * @property title - What could not be done, stated plainly ("Could not load articles")
 * @property description - Optional second line: why, and what to do about it
 * @property action - Optional control, typically the button that retries
 * @property className - Optional additional Tailwind classes
 */
export interface ErrorStateProps {
	title: ReactNode
	description?: ReactNode
	action?: ReactNode
	className?: string
}

/**
 * The placeholder a collection or page shows in place of its content when it could not be read.
 *
 * @remarks
 * - **Not an {@link EmptyState} variant.** A failed read says nothing about whether the
 *   collection is empty, and offering "create the first one" for a list that may be full
 *   would send the reader after the wrong problem.
 * - **Solid, tinted frame** rather than EmptyState's dashed one, so it never reads as a slot
 *   waiting to be filled. Same geometry otherwise, so swapping one for the other does not
 *   shift the layout.
 * - Announced as an `alert`; the glyph is decorative.
 *
 * @example
 * ```tsx
 * <ErrorState title="Could not load users" description={message} action={<Button onPress={retry}>Try again</Button>} />
 * ```
 *
 * @see packages/ui-components/src/error-state/error-state.stories.tsx for visual examples
 */
export function ErrorState({ title, description, action, className }: ErrorStateProps) {
	return (
		<div
			role="alert"
			className={cn(
				'rounded-ds-md border border-ds-danger/30 bg-ds-danger/5 p-6 text-center text-ds-sm text-ds-text-muted',
				className,
			)}
		>
			<TriangleAlert aria-hidden="true" className="mx-auto mb-2 h-5 w-5 text-ds-danger" />
			<p className="font-medium text-ds-text">{title}</p>
			{description ? <p className="mx-auto mt-1 max-w-md">{description}</p> : null}
			{action ? <div className="mt-3">{action}</div> : null}
		</div>
	)
}
